import { ARMOR_SLOTS, HOTBAR_SIZE, INV_SIZE } from "../config";
import { maxStack, itemDef, type ArmorSlot } from "./items";

export interface Slot {
  id: number;
  count: number;
  dur?: number;
}

export type SlotCell = Slot | null;

export const ARMOR_ORDER: ArmorSlot[] = ["head", "chest", "legs", "feet"];

export function makeSlot(id: number, count: number, dur?: number): Slot {
  const d = itemDef(id);
  return {
    id,
    count,
    dur: dur ?? d.durability,
  };
}

export function cloneSlot(s: SlotCell): SlotCell {
  if (!s) return null;
  return { id: s.id, count: s.count, dur: s.dur };
}

export function sameItem(a: SlotCell, b: SlotCell): boolean {
  if (!a || !b) return false;
  return a.id === b.id;
}

export function canStack(a: Slot, b: Slot): boolean {
  if (a.id !== b.id) return false;
  const max = maxStack(a.id);
  if (max <= 1) return false;
  return a.count < max;
}

export class Inventory {
  hotbar: SlotCell[] = Array.from({ length: HOTBAR_SIZE }, () => null);
  main: SlotCell[] = Array.from({ length: INV_SIZE }, () => null);
  armor: SlotCell[] = Array.from({ length: ARMOR_SLOTS }, () => null);
  craft: SlotCell[] = Array.from({ length: 4 }, () => null);
  table: SlotCell[] = Array.from({ length: 9 }, () => null);
  result: SlotCell = null;
  tableResult: SlotCell = null;
  cursor: SlotCell = null;
  selected = 0;

  allStorage(): SlotCell[] {
    return [...this.hotbar, ...this.main];
  }

  setStorage(slots: SlotCell[]): void {
    for (let i = 0; i < HOTBAR_SIZE; i++) this.hotbar[i] = slots[i] ?? null;
    for (let i = 0; i < INV_SIZE; i++) this.main[i] = slots[HOTBAR_SIZE + i] ?? null;
  }

  selectedSlot(): SlotCell {
    return this.hotbar[this.selected] ?? null;
  }

  add(id: number, count: number, dur?: number): number {
    let left = count;
    const max = maxStack(id);
    const piles = [...this.hotbar, ...this.main];
    for (const s of piles) {
      if (s && s.id === id && s.count < max) {
        const take = Math.min(max - s.count, left);
        s.count += take;
        left -= take;
        if (left <= 0) return 0;
      }
    }
    for (let i = 0; i < HOTBAR_SIZE && left > 0; i++) {
      if (!this.hotbar[i]) {
        const n = Math.min(max, left);
        this.hotbar[i] = makeSlot(id, n, dur);
        left -= n;
      }
    }
    for (let i = 0; i < INV_SIZE && left > 0; i++) {
      if (!this.main[i]) {
        const n = Math.min(max, left);
        this.main[i] = makeSlot(id, n, dur);
        left -= n;
      }
    }
    return left;
  }

  consumeSelected(n = 1): boolean {
    const s = this.hotbar[this.selected];
    if (!s || s.count < n) return false;
    s.count -= n;
    if (s.count <= 0) this.hotbar[this.selected] = null;
    return true;
  }

  consumeId(id: number, n: number): boolean {
    let need = n;
    const bags = [this.hotbar, this.main];
    for (const bag of bags) {
      for (const s of bag) {
        if (s && s.id === id) need -= s.count;
      }
    }
    if (need > 0) return false;
    need = n;
    for (const bag of bags) {
      for (let i = 0; i < bag.length && need > 0; i++) {
        const s = bag[i];
        if (!s || s.id !== id) continue;
        const take = Math.min(s.count, need);
        s.count -= take;
        need -= take;
        if (s.count <= 0) bag[i] = null;
      }
    }
    return true;
  }

  countOf(id: number): number {
    let n = 0;
    for (const s of this.allStorage()) if (s && s.id === id) n += s.count;
    return n;
  }

  damageSelected(amount = 1): void {
    const s = this.hotbar[this.selected];
    if (!s || s.dur === undefined) return;
    s.dur -= amount;
    if (s.dur <= 0) this.hotbar[this.selected] = null;
  }

  armorPoints(): number {
    let n = 0;
    for (const s of this.armor) {
      if (!s) continue;
      n += itemDef(s.id).armor ?? 0;
    }
    return n;
  }

  tryWear(slot: Slot): SlotCell {
    const def = itemDef(slot.id);
    if (!def.armorSlot) return slot;
    const idx = ARMOR_ORDER.indexOf(def.armorSlot);
    if (idx < 0) return slot;
    const prev = this.armor[idx] ?? null;
    this.armor[idx] = slot;
    return prev;
  }

  clickSlot(bag: SlotCell[], index: number, right: boolean, shift: boolean, into?: SlotCell[]): void {
    const cell = bag[index] ?? null;
    if (shift && cell && into) {
      const left = this.insertInto(into, cell);
      bag[index] = left;
      return;
    }
    if (right) {
      this.rightClick(bag, index);
      return;
    }
    if (!this.cursor && cell) {
      this.cursor = cell;
      bag[index] = null;
      return;
    }
    if (this.cursor && !cell) {
      bag[index] = this.cursor;
      this.cursor = null;
      return;
    }
    if (this.cursor && cell && sameItem(this.cursor, cell) && canStack(cell, this.cursor)) {
      const max = maxStack(cell.id);
      const take = Math.min(max - cell.count, this.cursor.count);
      cell.count += take;
      this.cursor.count -= take;
      if (this.cursor.count <= 0) this.cursor = null;
      return;
    }
    if (this.cursor && cell) {
      bag[index] = this.cursor;
      this.cursor = cell;
    }
  }

  private rightClick(bag: SlotCell[], index: number): void {
    const cell = bag[index] ?? null;
    if (!this.cursor && cell) {
      const half = Math.ceil(cell.count / 2);
      this.cursor = makeSlot(cell.id, half, cell.dur);
      cell.count -= half;
      if (cell.count <= 0) bag[index] = null;
      return;
    }
    if (this.cursor && !cell) {
      bag[index] = makeSlot(this.cursor.id, 1, this.cursor.dur);
      this.cursor.count -= 1;
      if (this.cursor.count <= 0) this.cursor = null;
      return;
    }
    if (this.cursor && cell && sameItem(this.cursor, cell) && cell.count < maxStack(cell.id)) {
      cell.count += 1;
      this.cursor.count -= 1;
      if (this.cursor.count <= 0) this.cursor = null;
    }
  }

  insertInto(bag: SlotCell[], slot: Slot): SlotCell {
    const max = maxStack(slot.id);
    let left = slot.count;
    for (const s of bag) {
      if (s && s.id === slot.id && s.count < max) {
        const take = Math.min(max - s.count, left);
        s.count += take;
        left -= take;
        if (left <= 0) return null;
      }
    }
    for (let i = 0; i < bag.length && left > 0; i++) {
      if (!bag[i]) {
        const n = Math.min(max, left);
        bag[i] = makeSlot(slot.id, n, slot.dur);
        left -= n;
      }
    }
    if (left <= 0) return null;
    return makeSlot(slot.id, left, slot.dur);
  }

  takeResult(fromTable: boolean): void {
    const res = fromTable ? this.tableResult : this.result;
    if (!res) return;
    if (!this.cursor) {
      this.cursor = cloneSlot(res);
    } else if (sameItem(this.cursor, res) && this.cursor.count + res.count <= maxStack(res.id)) {
      this.cursor.count += res.count;
    } else {
      return;
    }
    const grid = fromTable ? this.table : this.craft;
    for (let i = 0; i < grid.length; i++) {
      const s = grid[i];
      if (!s) continue;
      s.count -= 1;
      if (s.count <= 0) grid[i] = null;
    }
  }

  serialize(): unknown {
    return {
      hotbar: this.hotbar,
      main: this.main,
      armor: this.armor,
      selected: this.selected,
    };
  }

  load(data: {
    hotbar?: SlotCell[];
    main?: SlotCell[];
    armor?: SlotCell[];
    selected?: number;
  }): void {
    if (data.hotbar) this.hotbar = pad(data.hotbar, HOTBAR_SIZE);
    if (data.main) this.main = pad(data.main, INV_SIZE);
    if (data.armor) this.armor = pad(data.armor, ARMOR_SLOTS);
    if (data.selected !== undefined) this.selected = data.selected;
  }
}

function pad(arr: SlotCell[], n: number): SlotCell[] {
  const out = arr.slice(0, n);
  while (out.length < n) out.push(null);
  return out;
}
